import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { AxiosInstance } from 'axios';
import { type ApiError, addActionNameToMutationResult, useAxiosInstance } from 'shared/hooks/utils';
import { z } from 'zod/v4';
import {
  EMAIL_DOMAIN_QUERY_KEY,
  EMAIL_DOMAIN_URL,
  parseEmailDomain,
} from './use-load-email-domain';

const CreateEmailDomainVariablesSchema = z.object({
  domain: z.string().min(1),
});

export type CreateEmailDomainVariables = z.infer<typeof CreateEmailDomainVariablesSchema>;

export async function createEmailDomainFn(
  axiosInstance: Promise<AxiosInstance>,
  variables: CreateEmailDomainVariables,
) {
  const instance = await axiosInstance;
  const { data } = await instance.post(
    EMAIL_DOMAIN_URL,
    CreateEmailDomainVariablesSchema.parse(variables),
  );

  return parseEmailDomain(data);
}

export function useCreateEmailDomain() {
  const client = useQueryClient();
  const axiosInstance = useAxiosInstance();

  const mutation = useMutation<
    ReturnType<typeof parseEmailDomain>,
    ApiError,
    CreateEmailDomainVariables
  >({
    mutationFn: variables => createEmailDomainFn(axiosInstance, variables),
    onSuccess: () => {
      client.invalidateQueries({ queryKey: EMAIL_DOMAIN_QUERY_KEY });
    },
  });

  return addActionNameToMutationResult<
    ReturnType<typeof parseEmailDomain>,
    ApiError,
    CreateEmailDomainVariables,
    'createEmailDomain'
  >('createEmailDomain', mutation);
}
